import { usePreferences } from "../../contexts/PreferencesContext";
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import { MdFileDownload, MdOutlineSmartToy, MdPlayArrow, MdRefresh } from "react-icons/md";
import { BsFillEyeFill } from "react-icons/bs";
import { Link } from "react-router-dom";
import { saveAs } from "file-saver";
import moment from "moment";
import Request from "../../Request";
import Search from "../ReusableComponents/Search";

const EVENT_TYPES = [
  "AI_ASSISTANT",
  "BOOK",
  "CALCULATOR",
  "CELL_PHONE",
  "EARBUDS",
  "EMAIL",
  "EXTERNAL_WEBSITE",
  "HEADPHONES",
  "LAPTOP",
  "LONG_HAIR",
  "BROWSER_NEW_TAB",
  "LOOKING_AWAY",
  "MESSAGING",
  "MULTIPLE_FACES",
  "MULTIPLE_PEOPLE",
  "MULTIPLE_SPEAKERS",
  "NO_PERSON",
  "PAPER",
  "PERSON",
  "REFERENCE",
  "SEARCH_ENGINE",
  "SECOND_SPEAKER",
  "SPEECH",
  "START_MENU",
  "SMARTWATCH",
  "TV_MONITOR",
  "UNKNOWN",
];

const SOURCE_OPTIONS = ["all", "camera", "external_camera", "screen"];
const SORT_OPTIONS = ["date_asc", "date_desc", "events_desc", "events_asc"];

const formatSeconds = (value) => {
  const seconds = Number(value || 0);
  if (!Number.isFinite(seconds)) return "-";
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
};

const formatDateTime = (value) => value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "-";
const normalizeFilter = (value) => !value || value === "all" ? "" : value;

const VideoAIEvents = ({ processo_id, candidato_id = null, candidato_name = "", initial_source_origin = "all", initial_event_type = "all" }) => {
  const { t } = usePreferences();
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOficial, setSearchOficial] = useState("");
  const [eventType, setEventType] = useState(initial_event_type || "all");
  const [sourceOrigin, setSourceOrigin] = useState(initial_source_origin || "all");
  const [sort, setSort] = useState("date_asc");
  const [groups, setGroups] = useState([]);
  const [videos, setVideos] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [job, setJob] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const searchTimeout = useRef(null);
  const mode = candidato_id == null ? "processo" : "candidato";

  const translatedKey = (key, fallback = key) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const eventLabel = (event, fallback = "") => {
    if (event === "all" || event === "") return t("video_ai_all_event_types");
    if (event === "NO_FACE") return t("video_ai_event_no_person");
    return translatedKey(`video_ai_event_${String(event || "").toLowerCase()}`, fallback || event || "-");
  };

  const sourceLabel = (event) => {
    let source = event?.source_origin || event?.source_type || (event?.external || event?.is_external ? "external_camera" : "camera");
    if (source === "external") source = "external_camera";
    if (source !== "external_camera" && source !== "screen") source = "camera";
    return translatedKey(`video_ai_source_${source}`, source || "-");
  };

  const severityLabel = (severity) => translatedKey(`video_ai_severity_${severity || "medium"}`, severity || "-");
  const sourceOptionLabel = (source) => source === "all" ? t("video_ai_all_sources") : translatedKey(`video_ai_source_${source}`, source);
  const sortLabel = (value) => translatedKey(`video_ai_sort_${value}`, value);

  const loadEvents = async (page = 1, value = searchOficial, type = eventType, source = sourceOrigin, order = sort) => {
    setLoading(true);
    const query = `page=${page}&search=${encodeURIComponent(value)}&event_type=${encodeURIComponent(normalizeFilter(type))}&source_origin=${encodeURIComponent(normalizeFilter(source))}&sort=${encodeURIComponent(order)}`;
    const endpoint = mode === "processo"
      ? `faculdade/get_video_ai_events/${processo_id}?${query}`
      : `faculdade/get_video_ai_events_candidato/${processo_id}/${candidato_id}?${query}`;
    const response = await Request.Get(endpoint);

    if (response) {
      if (mode === "processo") {
        setGroups(response.candidatos || []);
      } else {
        setVideos(response.videos || []);
      }
      setPagination(response.pagination || { current_page: 1, last_page: 1 });
    }
    setLoading(false);
  };

  const loadJob = async () => {
    const response = await Request.Get(`faculdade/get_video_ai_events_job/${processo_id}`);
    if (response) setJob(response);
  };

  const generateEvents = async () => {
    setLoadingAction(true);
    const response = await Request.Post(`faculdade/generate_video_ai_events/${processo_id}`);
    if (response) setJob(response);
    setLoadingAction(false);
  };

  const downloadExcel = async () => {
    setLoadingAction(true);
    const query = `search=${encodeURIComponent(searchOficial)}&event_type=${encodeURIComponent(normalizeFilter(eventType))}&source_origin=${encodeURIComponent(normalizeFilter(sourceOrigin))}${candidato_id ? `&user_id=${candidato_id}` : ""}`;
    const response = await Request.GetFile(`faculdade/download_video_ai_events/${processo_id}?${query}`);
    if (response) {
      const filename = candidato_id
        ? `eventos_ia_videos_candidato_${candidato_id}_processo_${processo_id}.xlsx`
        : `eventos_ia_videos_processo_${processo_id}.xlsx`;
      saveAs(response, filename);
    }
    setLoadingAction(false);
  };

  useEffect(() => {
    loadEvents(1, "");
    loadJob();
  }, [processo_id, candidato_id]);

  const renderEventSummary = (items = []) => (
    <div className="video-ai-event-summary-list">
      {items.length === 0 ? (
        <span className="text-muted">-</span>
      ) : items.map((item) => (
        <span className="video-ai-badge event" key={item.event_type}>
          {eventLabel(item.event_type, item.label)}: {item.count}
        </span>
      ))}
    </div>
  );

  const candidateDetailsLink = (candidateId) => {
    const params = new URLSearchParams();
    params.set("tab", "8");
    if (sourceOrigin && sourceOrigin !== "all") params.set("source_origin", sourceOrigin);
    if (eventType && eventType !== "all") params.set("event_type", eventType);
    return `/candidatos_vinculados/show/${candidateId}/${processo_id}?${params.toString()}`;
  };

  const jobStatusLabel = (status) => {
    if (status === "0") return t("status_pending");
    if (status === "1") return t("status_in_progress");
    if (status === "2") return t("video_ai_job_success");
    if (status === "3") return t("status_execution_error");
    return status || t("video_ai_job_not_generated");
  };

  const showDetectedSiteColumn = mode === "candidato" && sourceOrigin === "screen";

  const renderGeneralRows = () => (
    <div className="table-modern-card video-ai-events-table">
      <div className="table-modern-scroll">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>{t("video_ai_snapshot")}</th>
              <th>{t("candidates")}</th>
              <th>{t("label_email")}</th>
              <th>{t("label_cpf")}</th>
              <th>{t("label_registration_number")}</th>
              <th>{t("video_ai_events")}</th>
              <th>{t("video_ai_points")}</th>
              <th>{t("label_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.id}>
                <td data-label={t("video_ai_snapshot")}>
                  {group.main_snapshot?.snapshot_url ? (
                    <img className="video-ai-snapshot-thumb" src={group.main_snapshot.snapshot_url} alt={group.main_snapshot.label || t("video_ai_snapshot")} />
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td data-label={t("candidates")}><b>{group.name || "-"}</b></td>
                <td data-label={t("label_email")}>{group.email || "-"}</td>
                <td data-label={t("label_cpf")}>{group.cpf || "-"}</td>
                <td data-label={t("label_registration_number")}>{group.numero_inscricao || "-"}</td>
                <td data-label={t("video_ai_events")}>{renderEventSummary(group.event_summaries || [])}</td>
                <td data-label={t("video_ai_points")}><span className="video-ai-badge points">{group.events_points || 0}</span></td>
                <td data-label={t("label_actions")}>
                  <div className="actions-group">
                    <Link className="action-btn info video-ai-eye-button" to={candidateDetailsLink(group.id)} target="_blank" rel="noopener noreferrer" title={t("video_ai_view_candidate_events")}>
                      <BsFillEyeFill size={16} />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderVideoRows = () => (
    <div className="table-modern-card video-ai-events-table">
      <div className="table-modern-scroll">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>{t("video_ai_snapshot")}</th>
              <th>{t("video_ai_events")}</th>
              {showDetectedSiteColumn && <th>{t("video_ai_detected_site")}</th>}
              <th>{t("video_ai_source")}</th>
              <th>{t("video_ai_clip")}</th>
              <th>{t("video_ai_datetime")}</th>
              <th>{t("video_ai_points")}</th>
              <th>{t("label_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video) => {
              const firstSnapshot = (video.events || []).find((event) => event.snapshot_url);
              const domains = [...new Set((video.events || []).map((event) => event.detected_domain).filter(Boolean))];
              return (
                <tr key={video.id}>
                  <td data-label={t("video_ai_snapshot")}>
                    {firstSnapshot?.snapshot_url ? (
                      <img className="video-ai-snapshot-thumb" src={firstSnapshot.snapshot_url} alt={t("video_ai_snapshot")} />
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td data-label={t("video_ai_events")}>{renderEventSummary(video.event_summaries || [])}</td>
                  {showDetectedSiteColumn && (
                    <td data-label={t("video_ai_detected_site")}>
                      {domains.length ? domains.map((domain) => <span className="video-ai-badge neutral" key={domain}>{domain}</span>) : "-"}
                    </td>
                  )}
                  <td data-label={t("video_ai_source")}>{sourceLabel(video)}</td>
                  <td data-label={t("video_ai_clip")}>{formatSeconds(video.start_seconds)} - {formatSeconds(video.end_seconds)}</td>
                  <td data-label={t("video_ai_datetime")}>{formatDateTime(video.event_at)}</td>
                  <td data-label={t("video_ai_points")}><span className="video-ai-badge points">{video.events_points || 0}</span></td>
                  <td data-label={t("label_actions")}>
                    <div className="actions-group">
                      <Button className="action-btn info video-ai-eye-button" size="sm" variant="primary" onClick={() => setSelectedVideo(video)} title={t("video_ai_view_details")}>
                        <BsFillEyeFill size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderEventDetails = () => {
    if (!selectedVideo) return null;
    const selectedEvents = selectedVideo.events || [];

    return (
      <Modal show={selectedVideo != null} onHide={() => setSelectedVideo(null)} size="xl" centered dialogClassName="video-ai-detail-modal">
        <Modal.Header closeButton closeLabel="">
          <Modal.Title>{t("video_ai_video_events")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="video-ai-detail-summary">
            {renderEventSummary(selectedVideo.event_summaries || [])}
            <span className="video-ai-badge neutral">{sourceLabel(selectedVideo)}</span>
            <span className="video-ai-badge points">{selectedVideo.events_points || 0} {t("video_ai_points").toLowerCase()}</span>
          </div>

          <div className="video-ai-detail-layout">
            <div className="video-ai-detail-media">
              <strong>{t("video_ai_video")}</strong>
              {selectedVideo.video_url ? (
                <video className="video-ai-detail-video" src={selectedVideo.video_url} controls preload="metadata" />
              ) : (
                <div className="video-ai-detail-empty">{t("video_ai_video_unavailable")}</div>
              )}
            </div>

            <div className="video-ai-detail-info">
              <div><span>{t("video_ai_first_event_datetime")}</span><b>{formatDateTime(selectedVideo.event_at)}</b></div>
              <div><span>{t("video_ai_analyzed_clip")}</span><b>{formatSeconds(selectedVideo.start_seconds)} - {formatSeconds(selectedVideo.end_seconds)}</b></div>
              <div><span>{t("video_ai_analysis_source")}</span><b>{sourceLabel(selectedVideo)}</b></div>
              <div><span>{t("video_ai_video_available")}</span><b>{selectedVideo.video_url ? t("summary_yes") : t("summary_no")}</b></div>
              <div><span>{t("video_ai_total_events")}</span><b>{selectedVideo.events_count || 0}</b></div>
            </div>
          </div>

          <div className="video-ai-detail-events">
            <strong>{t("video_ai_detected_events")}</strong>
            {selectedEvents.map((event) => (
              <div className="video-ai-detail-event-card" key={event.id}>
                <div className="video-ai-detail-event-image">
                  {event.snapshot_url ? (
                    <img src={event.snapshot_url} alt={eventLabel(event.event_type)} />
                  ) : (
                    <div className="video-ai-detail-empty">{t("video_ai_image_unavailable")}</div>
                  )}
                </div>
                <div className="video-ai-detail-event-content">
                  <div className="video-ai-detail-event-title">
                    <span className="video-ai-badge event">{eventLabel(event.event_type)}</span>
                    <span className={`video-ai-severity ${event.severity}`}>{severityLabel(event.severity)}</span>
                    <span className="video-ai-badge points">{event.points || 0} {t("video_ai_points").toLowerCase()}</span>
                  </div>
                  <div className="video-ai-detail-info compact">
                    <div><span>{t("video_ai_datetime")}</span><b>{formatDateTime(event.event_at)}</b></div>
                    <div><span>{t("video_ai_snapshot_second")}</span><b>{formatSeconds(event.snapshot_seconds)}</b></div>
                    <div><span>{t("video_ai_clip")}</span><b>{formatSeconds(event.start_seconds)} - {formatSeconds(event.end_seconds)}</b></div>
                    {event.detected_domain && <div><span>{t("video_ai_detected_site")}</span><b>{event.detected_domain}</b></div>}
                    <div><span>{t("video_ai_event_id")}</span><b>{event.id || "-"}</b></div>
                  </div>
                  <pre>{JSON.stringify(event.metadata || {}, null, 2)}</pre>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div className="video-ai-events">
      <div className="video-ai-events-header">
        <div>
          <h4 className="header-title">
            <MdOutlineSmartToy /> {t("video_ai_title")}
          </h4>
          <p className="text-muted">
            {mode === "processo" ? t("video_ai_process_description") : `${t("video_ai_candidate_description")} ${candidato_name}.`}
          </p>
          {job?.status && (
            <p className="video-ai-job-status">
              {t("video_ai_analysis_status")}: <b>{jobStatusLabel(job.status)}</b>
              {job.total_progresso ? ` (${job.progresso || 0}/${job.total_progresso})` : ""}
            </p>
          )}
        </div>

        <div className="video-ai-actions">
          {mode === "processo" && (
            <Button variant="success" onClick={generateEvents} disabled={loadingAction}>
              <MdPlayArrow /> {t("video_ai_generate_events")}
            </Button>
          )}
          <Button variant={mode === "candidato" ? "primary" : "secondary"} onClick={downloadExcel} disabled={loadingAction}>
            <MdFileDownload /> {t("video_ai_download_excel")}
          </Button>
          <Button variant="secondary" onClick={() => { loadJob(); loadEvents(pagination.current_page || 1, searchOficial, eventType, sourceOrigin, sort); }} disabled={loadingAction}>
            <MdRefresh /> {t("action_update")}
          </Button>
        </div>
      </div>

      <div className="video-ai-toolbar">
        <div className="video-ai-filter-field search-field">
          <label>{mode === "processo" ? t("video_ai_search_candidate") : t("video_ai_search_event")}</label>
          <Search
            hide_class
            placeholder={mode === "processo" ? t("video_ai_search_candidate_placeholder") : t("video_ai_search_event_placeholder")}
            ValueChange={(value) => {
              clearTimeout(searchTimeout.current);
              setSearch(value);
              searchTimeout.current = setTimeout(() => {
                setSearchOficial(value);
                loadEvents(1, value, eventType, sourceOrigin, sort);
              }, 500);
            }}
          />
        </div>
        <div className="video-ai-filter-field">
          <label>{t("video_ai_source")}</label>
          <select className="form-control video-ai-event-type-filter" value={sourceOrigin} onChange={(event) => {
            const value = event.target.value;
            setSourceOrigin(value);
            loadEvents(1, searchOficial, eventType, value, sort);
          }}>
            {SOURCE_OPTIONS.map((option) => <option key={option} value={option}>{sourceOptionLabel(option)}</option>)}
          </select>
        </div>
        <div className="video-ai-filter-field">
          <label>{t("video_ai_event_type")}</label>
          <select className="form-control video-ai-event-type-filter" value={eventType} onChange={(event) => {
            const value = event.target.value;
            setEventType(value);
            loadEvents(1, searchOficial, value, sourceOrigin, sort);
          }}>
            <option value="all">{t("video_ai_all_event_types")}</option>
            {EVENT_TYPES.map((option) => <option key={option} value={option}>{eventLabel(option)}</option>)}
          </select>
        </div>
        {mode === "candidato" && (
          <div className="video-ai-filter-field">
            <label>{t("video_ai_sort")}</label>
            <select className="form-control video-ai-event-type-filter" value={sort} onChange={(event) => {
              const value = event.target.value;
              setSort(value);
              loadEvents(1, searchOficial, eventType, sourceOrigin, value);
            }}>
              {SORT_OPTIONS.map((option) => <option key={option} value={option}>{sortLabel(option)}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading && <div className="table-state-spacing text-center"><CircularProgress /></div>}
      {!loading && mode === "processo" && groups.length > 0 && renderGeneralRows()}
      {!loading && mode === "candidato" && videos.length > 0 && renderVideoRows()}
      {!loading && ((mode === "processo" && groups.length === 0) || (mode === "candidato" && videos.length === 0)) && (
        <div className="empty-state table-state-spacing">
          <h4>{t("video_ai_no_events_found")}</h4>
          <p>{search ? t("video_ai_adjust_search") : t("video_ai_generate_to_view")}</p>
        </div>
      )}

      {pagination.last_page > 1 && (
        <Pagination
          style={{ display: "flex", justifyContent: "center", marginTop: 24 }}
          count={pagination.last_page}
          page={pagination.current_page}
          onChange={(_, value) => loadEvents(value, searchOficial, eventType, sourceOrigin, sort)}
        />
      )}

      {renderEventDetails()}
    </div>
  );
};

export default VideoAIEvents;
